import {some} from '@danwang/option';
import {useUpdateBalanceAnchorMutation} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

type Snapshot = {
  _id: string;
  date: string;
  value: number;
};

export type EditSnapshotModalUserProps = {
  snapshot: Snapshot;
  onSuccess?: () => void;
};

export const EditSnapshotModal = (
  props: EditSnapshotModalUserProps & SystemProps,
) => {
  const {snapshot} = props;
  const formId = React.useId();
  const [updateAnchor, {loading}] = useUpdateBalanceAnchorMutation();
  const [date, setDate] = React.useState<DateTime>(parseDate(snapshot.date));
  const [value, setValue] = React.useState<Option<Money>>(
    some(Money.fromInt(snapshot.value, 'USD')),
  );
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (value.isEmpty) {
      setError('Please enter a valid portfolio value');
      return;
    }

    try {
      await updateAnchor({
        variables: {
          anchorId: snapshot._id,
          date: date.toISODate() ?? '',
          balance: value.get().toInt().amount,
        },
      });
      props.onSuccess?.();
      props.onRequestClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update snapshot');
    }
  };

  const isValid = value.nonEmpty;

  const footer = (
    <>
      <Button
        variant="outline"
        disabled={loading}
        onClick={props.onRequestClose}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={loading || !isValid}
        type="submit"
        form={formId}
      >
        Save
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header="Edit snapshot"
      footer={footer}
    >
      <form id={formId} onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
        <InputGroup label="Date">
          <DatePicker selectedDate={date} onChange={setDate} />
        </InputGroup>
        <InputGroup label="Portfolio value">
          <AmountInput
            value={value}
            onChange={setValue}
            autoFocus
          />
        </InputGroup>
        {error && (
          <div className="text-destructive text-sm">{error}</div>
        )}
      </form>
    </Modal>
  );
};
